// 17/09 (Marlon) — « Vue calendrier » de la page Canal : stories et posts programmés, jour par jour, à l'heure de Paris.
// Logique pure (aucun appel réseau), testée sous node (scratchpad canal_calendrier/test_calendrier.py).
const TZ = 'Europe/Paris';
export const JOURS_SEMAINE = ['lun', 'mar', 'mer', 'jeu', 'ven', 'sam', 'dim'];
const MOIS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];

// Date du serveur (UTC, souvent sans « Z ») → Date. Illisible → null.
export function versDate(v) {
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;
  const s = String(v || '');
  if (!s) return null;
  const d = new Date(/Z$|[+-]\d{2}:?\d{2}$/.test(s) ? s : `${s}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

let formatJour = null;
// Jour à Paris « AAAA-MM-JJ » (un post à 00:30 à Paris le 1er = 22:30 UTC la veille → compté le 1er).
export function cleJourParis(v) {
  const d = versDate(v);
  if (!d) return '';
  formatJour = formatJour || new Intl.DateTimeFormat('en-GB', { timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit' });
  const p = {};
  formatJour.formatToParts(d).forEach(x => { p[x.type] = x.value; });
  return `${p.year}-${p.month}-${p.day}`;
}

// Mois affiché { annee, mois (1-12) } : celui de la date donnée, à Paris.
export function moisParis(v = new Date()) {
  const k = cleJourParis(v);
  return { annee: Number(k.slice(0, 4)), mois: Number(k.slice(5, 7)) };
}

export function decalerMois({ annee, mois }, n) {
  const t = annee * 12 + (mois - 1) + n;
  return { annee: Math.floor(t / 12), mois: (((t % 12) + 12) % 12) + 1 };
}

export function titreMois({ annee, mois }) {
  return `${MOIS[mois - 1]} ${annee}`;
}

export const prefixeMois = ({ annee, mois }) => `${annee}-${String(mois).padStart(2, '0')}-`;

// Grille du mois, lundi → dimanche : semaines complètes (jours du mois précédent / suivant en bordure).
export function grilleMois({ annee, mois }) {
  const decalage = (new Date(Date.UTC(annee, mois - 1, 1)).getUTCDay() + 6) % 7;   // lundi = 0
  const nbJours = new Date(Date.UTC(annee, mois, 0)).getUTCDate();
  const cases = Math.ceil((decalage + nbJours) / 7) * 7;
  const jours = [];
  for (let i = 0; i < cases; i += 1) {
    const d = new Date(Date.UTC(annee, mois - 1, 1 - decalage + i));
    jours.push({ cle: d.toISOString().slice(0, 10), jour: d.getUTCDate(), dansMois: d.getUTCMonth() === mois - 1 });
  }
  return jours;
}

// Période demandée au serveur : toute la grille + un jour de marge de chaque côté (le tri par jour se fait ensuite à l'heure de Paris).
export function periodeGrille(jours) {
  const sansMs = (ms) => new Date(ms).toISOString().replace(/\.\d{3}Z$/, 'Z');
  const debut = Date.parse(`${jours[0].cle}T00:00:00Z`) - 86400000;
  const fin = Date.parse(`${jours[jours.length - 1].cle}T00:00:00Z`) + 2 * 86400000;
  return { debut: sansMs(debut), fin: sansMs(fin) };
}

// Stories (GET /stories) et posts (GET /canal-posts) → une seule liste. Modèle d'un post = modèle rattachée à son canal
// (bot.account_id) ; canal sans modèle → null (visible seulement dans « Tous les modèles »). Les annulés sont retirés.
// 17/09 : un post du canal est « post » (photo/vidéo), « sondage » ou « vocal » (champ type du serveur, absent = photo/vidéo).
export const TYPES_CAL = ['story', 'post', 'sondage', 'vocal'];
export const ICONES = { story: '📸', post: '📢', sondage: '📊', vocal: '🎤' };
const typeDuPost = (r) => (r.type === 'sondage' || r.type === 'vocal' ? r.type : 'post');

export function elementsCalendrier(stories, posts, bots) {
  const modeleDuBot = {};
  (bots || []).forEach(b => { modeleDuBot[b.id] = b.account_id || null; });
  const s = (stories || []).map(r => ({
    id: `s_${r.id}`, type: 'story', quand: r.scheduled_at, cle: cleJourParis(r.scheduled_at), status: r.status || 'pending',
    modele: r.account_id || null, legende: r.caption || '', video: Boolean(r.is_video), media: r.url || null,
    vignette: r.vignette_url || null, cible: r.channel_id ? `📢 ${r.channel_name || 'Canal'}` : '👤 Profil', erreur: r.error || null,
  }));
  const p = (posts || []).map(r => ({
    id: `p_${r.id}`, type: typeDuPost(r), quand: r.scheduled_at, cle: cleJourParis(r.scheduled_at), status: r.status || 'pending',
    modele: modeleDuBot[r.bot_id] || null, legende: r.legende || '', video: Boolean(r.is_video), media: r.media_url || null,
    vignette: r.vignette_url || null, sondage: r.sondage || null, duree: r.duree_s || null,
    cible: `📢 ${r.chat_title || 'Canal'}`, erreur: r.error || null,
  }));
  return s.concat(p)
    .filter(x => x.cle && x.status !== 'canceled')
    .sort((a, b) => versDate(a.quand) - versDate(b.quand));
}

export function filtrerElements(elements, { modele = '', type = 'tout' } = {}) {
  return (elements || []).filter(x => (!modele || x.modele === modele) && (type === 'tout' || x.type === type));
}

// Compté = programmé, en cours d'envoi ou publié. Échec = à part (signalé discrètement).
const COMPTE = { pending: true, sending: true, posted: true };
export function compterParJour(elements) {
  const m = {};
  (elements || []).forEach(x => {
    if (!m[x.cle]) m[x.cle] = { story: 0, post: 0, sondage: 0, vocal: 0, echecs: 0 };
    const c = m[x.cle];
    if (x.status === 'failed') c.echecs += 1;
    else if (COMPTE[x.status]) c[x.type] += 1;
  });
  return m;
}

// Totaux du mois affiché (jours du mois seulement, pas ceux des semaines en bordure).
export function totauxMois(elements, mois) {
  const pref = prefixeMois(mois);
  const t = { story: 0, post: 0, sondage: 0, vocal: 0, publies: 0, echecs: 0 };
  (elements || []).forEach(x => {
    if (!x.cle.startsWith(pref)) return;
    if (x.status === 'failed') { t.echecs += 1; return; }
    if (!COMPTE[x.status]) return;
    t[x.type] += 1;
    if (x.status === 'posted') t.publies += 1;
  });
  return t;
}

const nb = (n, un, plusieurs) => `${n} ${n > 1 ? plusieurs : un}`;
const NOMS = { story: ['story', 'stories'], post: ['post', 'posts'], sondage: ['sondage', 'sondages'], vocal: ['vocal', 'vocaux'] };
export function texteTotaux(t, type = 'tout') {
  const n = (k) => t[k] || 0;
  if (type === 'story') return `${nb(n('story'), 'story', 'stories')} ${n('story') > 1 ? 'programmées' : 'programmée'} ce mois`;
  if (NOMS[type]) return `${nb(n(type), ...NOMS[type])} ${n(type) > 1 ? 'programmés' : 'programmé'} ce mois`;
  // Tout : stories et posts toujours ; sondages et vocaux seulement s'il y en a.
  const morceaux = [nb(n('story'), 'story', 'stories'), nb(n('post'), 'post', 'posts')];
  if (n('sondage')) morceaux.push(nb(n('sondage'), ...NOMS.sondage));
  if (n('vocal')) morceaux.push(nb(n('vocal'), ...NOMS.vocal));
  return `${morceaux.join(' · ')} programmés ce mois`;
}

export function texteJour(c) {
  const morceaux = [];
  TYPES_CAL.forEach(k => { if (c[k]) morceaux.push(nb(c[k], ...NOMS[k])); });
  if (c.echecs) morceaux.push(nb(c.echecs, 'échec', 'échecs'));
  return morceaux.join(' · ') || 'rien de prévu';
}

let formatLibelle = null;
// « mercredi 17 septembre » pour une clé AAAA-MM-JJ.
export function libelleJour(cle) {
  const d = new Date(`${cle}T12:00:00Z`);
  if (Number.isNaN(d.getTime())) return cle;
  formatLibelle = formatLibelle || new Intl.DateTimeFormat('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' });
  return formatLibelle.format(d);
}

// Cases de la grille (ordinateur) : jusqu'à 3 mini-vignettes dans l'ordre de l'heure, puis « +N ».
export function miniaturesJour(elementsDuJour, max = 3) {
  const liste = elementsDuJour || [];
  return { montres: liste.slice(0, max), reste: Math.max(0, liste.length - max) };
}

export function legendeCourte(texte, max = 90) {
  const t = String(texte || '').replace(/\s+/g, ' ').trim();
  return t.length > max ? `${t.slice(0, max - 1)}…` : t;
}
